import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/atoms/Button';
import { SearchInput } from '../ui/search-input';
import { Tag } from '../ui/atoms/Tag';

interface SkillOption {
  id: string;
  name: string;
}

interface AddQuestionDropdownProps {
  skills: SkillOption[];
  onAddWithSkill: (skillIdOrName: string) => void;
  /** Render as a full-width inline zone instead of a compact button with floating dropdown */
  zone?: boolean;
  /** Called when the zone open state changes (zone mode only) */
  onOpenChange?: (open: boolean) => void;
}

export const AddQuestionDropdown = ({ skills, onAddWithSkill, zone, onOpenChange }: AddQuestionDropdownProps) => {
  const { t } = useTranslation('job');
  const [open, setOpen] = useState(false);

  const setOpenWithCallback = (value: boolean) => {
    setOpen(value);
    onOpenChange?.(value);
  };
  const [search, setSearch] = useState('');
  const buttonRef = useRef<HTMLButtonElement>(null);
  const zoneRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const filtered = search.trim()
    ? skills.filter((s) => s.name.toLowerCase().includes(search.toLowerCase()))
    : skills;

  // Position the floating menu above the button (non-zone mode only)
  useEffect(() => {
    if (zone || !open || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    setPos({ top: rect.top - 8, left: rect.left });
  }, [open, zone]);

  // Reset search when closing
  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  // Close on outside click or Escape
  useEffect(() => {
    if (!open) return;
    const ref = zone ? zoneRef : menuRef;
    const triggerRef = zone ? zoneRef : buttonRef;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setOpenWithCallback(false);
    };
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpenWithCallback(false); };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, zone]);

  const addSkillLabel = t('exam_config.add_question.add_skill_label');

  // ── Zone mode: inline expansion ──
  if (zone) {
    if (!open) {
      return (
        <Button
          ref={buttonRef}
          type="outlined"
          intent="secondary"
          size="md"
          dashed
          className="w-full"
          icon={<Sparkles />}
          label={addSkillLabel}
          onClick={() => setOpenWithCallback(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenWithCallback(true); }
          }}
        />
      );
    }

    return (
      <div ref={zoneRef} className="w-full rounded-xl border-2 border-dashed border-dark-invirtus/30 bg-white p-4 transition-all">
        <div className="flex items-center gap-2 mb-3">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={t('exam_config.add_question.search_skills')}
            size="sm"
            autoFocus
            className="flex-1"
          />
          <Button
            type="icon"
            intent="muted"
            size="sm"
            onClick={() => setOpenWithCallback(false)}
            aria-label={t('exam_config.actions.close')}
            icon={<X />}
          />
        </div>

        <div className="max-h-40 overflow-y-auto">
          {filtered.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {filtered.map((skill) => (
                <button
                  key={skill.id}
                  type="button"
                  onClick={() => { onAddWithSkill(skill.id); setOpenWithCallback(false); }}
                  className="inline-flex rounded-full transition-colors hover:[&>span]:bg-purple/10 hover:[&>span]:text-purple"
                >
                  <Tag intent="secondary" variant="subtle">
                    {skill.name}
                  </Tag>
                </button>
              ))}
            </div>
          ) : (
            <p className="py-2 text-xs font-body text-dark-invirtus/40 italic">{t('exam_config.add_question.no_matching_skills')}</p>
          )}
        </div>
      </div>
    );
  }

  // ── Button mode: compact button + floating dropdown ──
  return (
    <>
      <Button
        ref={buttonRef}
        type="outlined"
        intent="secondary"
        size="sm"
        icon={<Sparkles/>}
        label={addSkillLabel}
        onClick={() => setOpen(!open)}
      />
      {open && createPortal(
        <div
          ref={menuRef}
          className="fixed w-72 bg-white border border-dark-invirtus/15 rounded-lg z-[9999]"
          style={{
            top: pos.top,
            left: pos.left,
            transform: 'translateY(-100%)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.10), 0 1px 3px rgba(0,0,0,0.06)',
          }}
        >
          <div className="p-2">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder={t('exam_config.add_question.search_skills')}
              size="sm"
              autoFocus
              className="mb-1.5"
            />

            <p className="text-[10px] font-heading font-semibold text-dark-invirtus/40 uppercase tracking-wider px-2 pt-1 pb-1.5">
              <Sparkles className="w-3 h-3 inline-block mr-1 -mt-0.5" />
              {t('exam_config.add_question.ai_generates_for_skill')}
            </p>
            <div className="max-h-56 overflow-y-auto">
              {filtered.length > 0 ? (
                <div className="flex flex-wrap gap-1.5 px-1 pb-1">
                  {filtered.map((skill) => (
                    <button
                      key={skill.id}
                      type="button"
                      onClick={() => { onAddWithSkill(skill.id); setOpenWithCallback(false); }}
                      className="inline-flex rounded-full transition-colors hover:[&>span]:bg-purple/10 hover:[&>span]:text-purple"
                    >
                      <Tag intent="secondary" variant="subtle">
                        {skill.name}
                      </Tag>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="px-3 py-2 text-xs font-body text-dark-invirtus/40 italic">{t('exam_config.add_question.no_matching_skills')}</p>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};
